// @ts-nocheck
import { useState, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Button, Drawer, Form, Input, message,
  Avatar, Dropdown,
} from "../components/admin-ui";
import { Checkbox } from "../components/admin-ui";
import type { MenuProps } from "../components/admin-ui";
import type { ColumnsType } from "../components/admin-ui";
import {
  PlusOutlined, UserOutlined, SafetyCertificateOutlined,
  MailOutlined, LockOutlined, MoreOutlined, TeamOutlined,
  CheckCircleOutlined, CloseCircleOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
} from "../components/admin-icons";
import type { Staff } from "@kawkaw/shared";
import { ADMIN_GRANTABLE_PERMISSIONS } from "@kawkaw/shared";
import { DataTable } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { AdminShell } from "../layouts/AdminShell";
import { useAdminStaff, useCreateStaff, useUpdateStaff, useDeleteStaff } from "../api/hooks";
import { tokens } from "../styles/tokens";
import { useConfirmDialog } from "../components/ConfirmDialog";
const { color: T } = tokens;
import { useAuthStore } from "../store/auth";
import { getInitials, getAvatarColor, formatLastLogin } from "../utils/staffDisplay";

/* ─── Language-aware permission label ────────────────────────────────────── */
function permLabel(p: (typeof ADMIN_GRANTABLE_PERMISSIONS)[number], lang: string): string {
  if (lang.startsWith("zh")) return p.labelZh;
  if (lang.startsWith("ms")) return p.labelMs;
  return p.labelEn;
}

/* ─── DrawerSection helper (same presentational wrapper as Staff.tsx) ────── */
function DrawerSection({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="admin-admin-drawer-section">
      <div className="admin-admin-drawer-section-title">
        <span>{icon}</span>
        <span>{title}</span>
      </div>
      {children}
    </section>
  );
}

function AdminAccountStatCard({ label, value, icon, iconColor, badge, badgeColor, badgeBg }: {
  label: string;
  value: number;
  icon: ReactNode;
  iconColor: string;
  badge: string;
  badgeColor: string;
  badgeBg: string;
}) {
  return (
    <div className="admin-kpi-card admin-admin-account-stat">
      <div className="admin-kpi-heading flex">
        <div className="admin-kpi-label">{label}</div>
        <span className="admin-kpi-icon" style={{ color: iconColor, background: badgeBg }}>{icon}</span>
      </div>
      <div className="admin-kpi-value">{value.toLocaleString()}</div>
      <div className="admin-admin-account-stat-note" style={{ color: badgeColor }}>{badge}</div>
    </div>
  );
}

/* ─── Add / Edit Drawer ───────────────────────────────────── */
// Only "admin" is creatable via this UI (matches the backend's own
// privilege-escalation guard — superadmin can never be granted via API).
// Editing an existing superadmin shows a locked badge instead of any
// role/permission control, and never sends role/permissions on save —
// structurally can't reintroduce the demotion bug this replaces.
type DrawerFormValues = { name: string; phone: string; email: string; password?: string };

function AddEditAdminDrawer({ open, editing, onClose, onSaved }: {
  open: boolean; editing: Staff | null; onClose: () => void; onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm<DrawerFormValues>();
  const [permissions, setPermissions] = useState<string[]>([]);
  const createStaff = useCreateStaff();
  const updateStaff = useUpdateStaff();
  const isEdit = !!editing;
  const isSuperadmin = editing?.role === "superadmin";

  const handleAfterOpen = (visible: boolean) => {
    if (!visible) return;
    if (editing) {
      form.setFieldsValue({ name: editing.name, phone: editing.phone, email: editing.email ?? "", password: "" });
      setPermissions(editing.permissions ?? []);
    } else {
      form.resetFields();
      setPermissions(ADMIN_GRANTABLE_PERMISSIONS.map((p) => p.key)); // sensible default: full access, superadmin can narrow after creation
    }
  };

  const togglePermission = (key: string) => {
    setPermissions((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]));
  };

  const onFinish = async (values: DrawerFormValues) => {
    try {
      if (isEdit && editing) {
        await updateStaff.mutateAsync({
          id: editing.id, name: values.name,
          ...(isSuperadmin ? {} : { permissions }),
          ...(values.password ? { password: values.password } : {}),
        });
        void message.success(t("staff.updatedOk"));
      } else {
        await createStaff.mutateAsync({
          name: values.name, phone: values.phone, role: "admin",
          email: values.email, password: values.password ?? "", permissions,
        });
        void message.success(t("staff.addedOk"));
      }
      onSaved();
    } catch {
      void message.error(t("staff.saveFailed"));
    }
  };

  const loading = createStaff.isPending || updateStaff.isPending;

  return (
    <Drawer
      open={open} onClose={onClose} placement="right" width={440}
      closable={false} afterOpenChange={handleAfterOpen}
      styles={{
        body: { padding: "20px 20px 100px", background: T.bg },
        header: { display: "none" },
        footer: { padding: 0, border: "none" },
      }}
      footer={
        <div className="admin-admin-drawer-actions">
          <Button onClick={onClose}>{t("staff.cancel")}</Button>
          <Button type="primary" onClick={() => void form.submit()} loading={loading}>
            {isEdit ? t("staff.saveChanges") : t("staff.saveStaff")}
          </Button>
        </div>
      }
    >
      <div className="admin-admin-drawer-heading">
        <div>
          <span className="admin-admin-drawer-title">{isEdit ? t("staff.editAdmin") : t("staff.addAdminBtn")}</span>
          <p>{isEdit ? t("staff.adminDrawerSubtitleEdit") : t("staff.adminDrawerSubtitleNew")}</p>
        </div>
        <button className="admin-admin-drawer-close" type="button" onClick={onClose} aria-label={t("staff.close")}>×</button>
      </div>

      <Form className="admin-admin-account-form" form={form} layout="vertical" colon={false} onFinish={onFinish}>
        <DrawerSection icon={<UserOutlined />} title={t("staff.personalInfo")}>
          <Form.Item name="name"
            label={<span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5 }}>{t("staff.fullName")} <span style={{ color: T.brand }}>*</span></span>}
            rules={[{ required: true, message: t("staff.fullNameRequired") }]} style={{ marginBottom: 14 }}>
            <Input placeholder={t("staff.placeholderFullName")} style={{ height: 44, borderRadius: 10, fontSize: 14 }} />
          </Form.Item>
          <Form.Item name="phone"
            label={<span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5 }}>{t("staff.phoneNumber")} <span style={{ color: T.brand }}>*</span></span>}
            rules={[{ required: true, message: t("staff.phoneRequired") }]} style={{ marginBottom: 14 }}>
            <Input placeholder={t("staff.placeholderPhone")} disabled={isEdit} style={{ height: 44, borderRadius: 10, fontSize: 14 }} />
          </Form.Item>
          <Form.Item name="email"
            label={<span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5 }}>{t("staff.colEmail")} <span style={{ color: T.brand }}>*</span></span>}
            rules={[{ required: true, message: t("staff.emailRequired") }, { type: "email", message: t("staff.emailRequired") }]} style={{ marginBottom: 0 }}>
            <Input prefix={<MailOutlined />} placeholder={t("staff.placeholderEmail")} style={{ height: 44, borderRadius: 10, fontSize: 14 }} />
          </Form.Item>
        </DrawerSection>

        <DrawerSection icon={<LockOutlined />} title={t("staff.passwordLabel")}>
          <Form.Item name="password"
            rules={isEdit ? [] : [{ required: true, message: t("staff.passwordRequired") }, { min: 8, message: t("staff.passwordMinLength") }]}
            style={{ marginBottom: 0 }}>
            <Input.Password
              placeholder={isEdit ? t("staff.passwordPlaceholderEdit") : t("staff.passwordPlaceholderCreate")}
              style={{ height: 44, borderRadius: 10, fontSize: 14 }} />
          </Form.Item>
        </DrawerSection>

        <DrawerSection icon={<SafetyCertificateOutlined />} title={t("staff.permissionsTitle")}>
          {isSuperadmin ? (
            <div className="admin-admin-superadmin-note">
              {t("staff.superadminLockedHint")}
            </div>
          ) : (
            <>
              <div className="admin-admin-permissions-hint">{t("staff.permissionsHint")}</div>
              <div className="admin-admin-permissions-list">
                {ADMIN_GRANTABLE_PERMISSIONS.map((p) => (
                  <Checkbox key={p.key} checked={permissions.includes(p.key)} onChange={() => togglePermission(p.key)}>
                    {permLabel(p, i18n.language)}
                  </Checkbox>
                ))}
              </div>
            </>
          )}
        </DrawerSection>
      </Form>
    </Drawer>
  );
}

/* ─── Screen ─────────────────────────────────────────────── */
type StatusFilter = "all" | "active" | "inactive";

export function AdminAccountsScreen() {
  const { t, i18n } = useTranslation();
  const { confirm: confirmDialog } = useConfirmDialog();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<Staff | null>(null);

  const { data, isLoading } = useAdminStaff();
  const deleteStaff = useDeleteStaff();
  const meId = useAuthStore((s) => s.staff?.id);

  // Only admin/superadmin rows — operational staff (pos_operator/stall_staff)
  // are managed on the separate Staff screen (/staff).
  const allAdmins: Staff[] = (data?.data ?? []).filter(s => s.role === "admin" || s.role === "superadmin");

  const totalAdmins   = allAdmins.length;
  const activeCount   = allAdmins.filter(s => s.is_active).length;
  const inactiveCount = allAdmins.filter(s => !s.is_active).length;

  const filteredAdmins = useMemo(() => allAdmins.filter(s => {
    if (search) {
      const q = search.toLowerCase();
      if (!s.name.toLowerCase().includes(q) && !s.phone.includes(q) && !(s.email ?? "").toLowerCase().includes(q)) return false;
    }
    if (statusFilter === "active"   && !s.is_active) return false;
    if (statusFilter === "inactive" &&  s.is_active) return false;
    return true;
  }), [allAdmins, search, statusFilter]);

  const openCreate = () => { setEditingAdmin(null); setDrawerOpen(true); };
  const openEdit   = (s: Staff) => { setEditingAdmin(s); setDrawerOpen(true); };

  const columns: ColumnsType<Staff> = [
    {
      title: t("staff.colStaffDetails"),
      key: "details", width: 220,
      render: (_, row) => (
        <div className="admin-admin-account-identity">
          <Avatar size={38} style={{ background: getAvatarColor(row.name), fontSize: 13, fontWeight: 900, flexShrink: 0 }}>
            {getInitials(row.name)}
          </Avatar>
          <div className="admin-admin-account-copy">
            <div className="admin-data-customer">{row.name}</div>
            <div className="admin-data-secondary">{row.email ?? row.phone}</div>
          </div>
        </div>
      ),
    },
    {
      title: t("staff.colRole"),
      dataIndex: "role", key: "role", width: 110,
      render: (v: string) => (
        <span className="admin-data-badge admin-admin-role-badge">
          {v === "superadmin" ? t("staff.superadminBadge") : t("staff.roleAdmin")}
        </span>
      ),
    },
    {
      title: t("staff.colPermissions"),
      dataIndex: "permissions", key: "permissions", width: 220,
      render: (perms: string[] | undefined, row) => {
        if (row.role === "superadmin") return <span className="admin-data-secondary">{t("staff.allPermissions")}</span>;
        const list = perms ?? [];
        if (!list.length) return <span className="admin-data-secondary">{t("staff.noPermissions")}</span>;
        const labels = ADMIN_GRANTABLE_PERMISSIONS.filter(p => list.includes(p.key)).map(p => permLabel(p, i18n.language));
        const shown = labels.slice(0, 2);
        const extra = labels.length - shown.length;
        return (
          <div className="admin-admin-permission-badges">
            {shown.map(l => (
              <span className="admin-data-badge" key={l}>{l}</span>
            ))}
            {extra > 0 && <span className="admin-data-secondary">{t("staff.morePermissions", { count: extra })}</span>}
          </div>
        );
      },
    },
    {
      title: t("staff.colStatus"),
      dataIndex: "is_active", key: "status", width: 100,
      render: (v: boolean) => <StatusBadge status={v ? "active" : "inactive"} kind="staff" label={v ? t("staff.statusActive") : t("staff.statusInactive")} />,
    },
    {
      title: t("staff.colLastLogin"),
      dataIndex: "last_login_at", key: "lastLogin", width: 130,
      render: (v: string | null) => <span className="admin-data-time">{formatLastLogin(v, t, i18n.language)}</span>,
    },
    {
      title: t("staff.colActions"),
      key: "actions", width: 70, align: "center",
      render: (_, row) => {
        const items: MenuProps["items"] = [
          { key: "edit", icon: <EditOutlined />, label: t("staff.edit"), onClick: () => openEdit(row) },
          { type: "divider" },
          {
            key: "delete", danger: true, disabled: row.id === meId || row.role === "superadmin",
            icon: <DeleteOutlined />, label: t("staff.removeStaff"),
            onClick: async () => {
              const ok = await confirmDialog({ title: t("staff.removeStaff"), description: row.name, confirmLabel: t("staff.removeStaff"), danger: true });
              if (!ok) return;
              try {
                await deleteStaff.mutateAsync(row.id);
                void message.success(t("staff.removedOk"));
              } catch {
                void message.error(t("staff.removeFailed"));
              }
            },
          },
        ];
        return (
          <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
            <Button className="admin-admin-account-more admin-data-more" type="text"
              aria-label={`${t("staff.colActions")} ${row.name}`} icon={<MoreOutlined style={{ fontSize: 18 }} />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <AdminShell>
      <div className="admin-admin-accounts-page admin-list-page">
        <div className="admin-admin-accounts-page-inner">
          <div className="admin-admin-accounts-toolbar">
            <div>
              <h1 className="admin-page-title">{t("staff.adminAccountsTitle")}</h1>
              <p className="admin-admin-accounts-subtitle">{t("staff.adminAccountsSubtitle")}</p>
            </div>
            <Button className="admin-admin-accounts-create" type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {t("staff.addAdminBtn")}
            </Button>
          </div>

          <section className="admin-admin-accounts-stats admin-txn-stats grid">
            <AdminAccountStatCard label={t("staff.adminStatTotal")} value={totalAdmins} icon={<TeamOutlined />} iconColor={T.brand} badge={t("staff.adminStatBadgeOverall")} badgeColor={T.brandAmberText} badgeBg={T.warningBg} />
            <AdminAccountStatCard label={t("staff.adminStatActive")} value={activeCount} icon={<CheckCircleOutlined />} iconColor={T.successText} badge={t("staff.adminStatBadgeActive")} badgeColor={T.successText} badgeBg={T.successBg} />
            <AdminAccountStatCard label={t("staff.adminStatInactive")} value={inactiveCount} icon={<CloseCircleOutlined />} iconColor={T.textSecondary} badge={t("staff.adminStatBadgeInactive")} badgeColor={T.textSecondary} badgeBg={T.surfaceSection} />
          </section>

          <div className="admin-admin-accounts-filters admin-txn-filter-panel">
            <div className="admin-admin-search-field">
              <span><SearchOutlined /></span>
              <input
                className="admin-admin-search-input"
                placeholder={t("staff.searchPlaceholder")}
                value={search} onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select className="admin-admin-status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value as StatusFilter)}>
              <option value="all">{t("staff.allStatus")}</option>
              <option value="active">{t("staff.activeStatus")}</option>
              <option value="inactive">{t("staff.inactiveStatus")}</option>
            </select>
          </div>

          <p className="admin-admin-accounts-results">{t("common.total", { count: filteredAdmins.length })}</p>
          <div className="admin-admin-accounts-table-panel admin-data-table-panel">
            <DataTable<Staff> columns={columns} dataSource={filteredAdmins} loading={isLoading} rowKey="id" sticky={false} />
          </div>

          <AddEditAdminDrawer open={drawerOpen} editing={editingAdmin} onClose={() => setDrawerOpen(false)} onSaved={() => setDrawerOpen(false)} />
        </div>
      </div>
    </AdminShell>
  );
}
